import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Target, Plus, Trash2, Edit2, Check, X, Loader2, Users, ChevronDown,
  ChevronUp, BarChart3, CheckCircle, Circle
} from "lucide-react";

export default function ChecklistTab({ selectedProject }) {
  const { api, profile, isAdmin } = useAuth();
  const [groups, setGroups] = useState([]);
  const [progress, setProgress] = useState(null);
  const [allProgress, setAllProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: "", marketer_names: "",
    targets: { promotional_posts: 10, promotional_comments: 10, non_promotional_posts: 5, non_promotional_comments: 5 }
  });
  const [error, setError] = useState("");
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [logForm, setLogForm] = useState({ entry_type: "promotional_comments", account: "", count: 1 });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [gRes, pRes] = await Promise.all([
        api.get("/checklist/groups"),
        isAdmin ? api.get("/checklist/progress/all") : api.get("/checklist/progress"),
      ]);
      setGroups(gRes.data);
      if (isAdmin) setAllProgress(pRes.data);
      else setProgress(pRes.data);
    } catch {} finally { setLoading(false); }
  };

  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) return;
    setError("");
    try {
      const names = groupForm.marketer_names.split(",").map(n => n.trim()).filter(Boolean);
      await api.post("/checklist/groups", { name: groupForm.name, targets: groupForm.targets, marketer_names: names });
      setShowGroupForm(false);
      setGroupForm({ name: "", marketer_names: "", targets: { promotional_posts: 10, promotional_comments: 10, non_promotional_posts: 5, non_promotional_comments: 5 } });
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Failed"); }
  };

  const handleUpdateGroup = async (gid) => {
    setError("");
    try {
      const names = groupForm.marketer_names.split(",").map(n => n.trim()).filter(Boolean);
      await api.patch(`/checklist/groups/${gid}`, { name: groupForm.name, targets: groupForm.targets, marketer_names: names });
      setEditingGroup(null);
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteGroup = async (gid) => {
    if (!window.confirm("Delete this group?")) return;
    try { await api.delete(`/checklist/groups/${gid}`); fetchAll(); } catch {}
  };

  const handleLogProgress = async () => {
    if (!logForm.account.trim()) return;
    try {
      await api.post("/checklist/progress/log", { group_id: "", entry_type: logForm.entry_type, account: logForm.account, count: logForm.count });
      if (isAdmin) { const res = await api.get("/checklist/progress/all"); setAllProgress(res.data); }
      else { const res = await api.get("/checklist/progress"); setProgress(res.data); }
    } catch {}
  };

  const startEdit = (g) => {
    setEditingGroup(g.id);
    setGroupForm({ name: g.name, marketer_names: (g.marketer_names || []).join(", "), targets: { ...g.targets } });
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  const targetKeys = ["promotional_posts", "promotional_comments", "non_promotional_posts", "non_promotional_comments"];

  return (
    <div className="checklist-section" data-testid="checklist-section">
      {/* Daily Report Summary */}
      {!isAdmin && progress && (
        <div className="checklist-my-progress" data-testid="my-daily-checklist">
          <h2 className="section-title"><Target size={20} color="#ff4500" /> My Daily Checklist — {progress.date}</h2>
          <p className="section-desc">Group: <strong>{progress.group}</strong></p>
          <div className="checklist-items">
            {progress.items.map(item => (
              <div key={item.type} className={`checklist-item ${item.completed ? 'completed' : ''}`} data-testid={`checklist-${item.type}`}>
                <div className="checklist-icon">
                  {item.completed ? <CheckCircle size={18} color="#10b981" /> : <Circle size={18} color="#4b5563" />}
                </div>
                <div className="checklist-info">
                  <div className="checklist-label">{item.label}</div>
                  <div className="checklist-progress-bar">
                    <div className="checklist-progress-fill" style={{ width: `${Math.min(100, (item.done / item.target) * 100)}%` }}></div>
                  </div>
                </div>
                <div className="checklist-count">{item.done}/{item.target}</div>
              </div>
            ))}
          </div>

          <div className="log-progress-form" data-testid="log-progress">
            <h4 style={{ margin: '16px 0 8px', fontSize: 13, color: '#9ca3af' }}>Log Progress</h4>
            <div className="form-row">
              <select className="filter-select" value={logForm.entry_type} onChange={e => setLogForm({ ...logForm, entry_type: e.target.value })}>
                {targetKeys.map(k => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
              </select>
              <input className="filter-input" placeholder="Account name" value={logForm.account} onChange={e => setLogForm({ ...logForm, account: e.target.value })} />
              <input className="filter-input" type="number" min="1" value={logForm.count} onChange={e => setLogForm({ ...logForm, count: parseInt(e.target.value) || 1 })} style={{ width: 60 }} />
              <button className="btn btn-sm btn-primary" onClick={handleLogProgress}><Plus size={13} /> Log</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin: All Marketers Progress */}
      {isAdmin && allProgress && (
        <div className="admin-progress-view" data-testid="all-marketers-progress">
          <h2 className="section-title"><BarChart3 size={20} color="#ff4500" /> Daily Progress — {allProgress.date}</h2>
          {allProgress.marketers.length === 0 ? (
            <div className="empty-state"><BarChart3 size={40} /><div className="empty-text">No progress logged today</div></div>
          ) : (
            <div className="table-container">
              <table className="comments-table" data-testid="progress-table">
                <thead>
                  <tr>
                    <th>Marketer</th><th>Group</th>
                    <th>Promo Posts</th><th>Promo Comments</th><th>Non-Promo Posts</th><th>Non-Promo Comments</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allProgress.marketers.map(m => (
                    <tr key={m.marketer_name}>
                      <td><span className="marketer-cell">{m.marketer_name}</span></td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{m.group}</td>
                      {m.items.map(i => (
                        <td key={i.type}>
                          <span className={`checklist-mini ${i.completed ? 'done' : ''}`}>{i.done}/{i.target}</span>
                        </td>
                      ))}
                      <td>{m.overall_complete ? <CheckCircle size={16} color="#10b981" /> : <Circle size={16} color="#4b5563" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="log-progress-form" data-testid="admin-log-progress" style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#9ca3af' }}>Log Progress (Admin)</h4>
            <div className="form-row">
              <select className="filter-select" value={logForm.entry_type} onChange={e => setLogForm({ ...logForm, entry_type: e.target.value })} data-testid="log-type-select">
                {targetKeys.map(k => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
              </select>
              <input className="filter-input" placeholder="Account name" value={logForm.account}
                onChange={e => setLogForm({ ...logForm, account: e.target.value })} data-testid="log-account-input" />
              <input className="filter-input" type="number" min="1" value={logForm.count}
                onChange={e => setLogForm({ ...logForm, count: parseInt(e.target.value) || 1 })} style={{ width: 60 }} data-testid="log-count-input" />
              <button className="btn btn-sm btn-primary" onClick={handleLogProgress} data-testid="log-progress-btn">
                <Plus size={13} /> Log
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Groups Management (Admin) */}
      {isAdmin && (
        <div className="groups-section" style={{ marginTop: 24 }}>
          <div className="section-header">
            <h2 className="section-title"><Users size={20} color="#ff4500" /> Checklist Groups</h2>
            <button className="btn btn-sm btn-primary" onClick={() => { setShowGroupForm(!showGroupForm); setEditingGroup(null); }} data-testid="add-group-btn">
              <Plus size={14} /> New Group
            </button>
          </div>

          {error && <div className="alert alert-error" style={{ margin: '8px 0' }}><X size={14} /> {error}</div>}

          {(showGroupForm || editingGroup) && (
            <div className="marketer-form" data-testid="group-form">
              <h4 style={{ margin: '0 0 12px', color: '#e5e5e5', fontSize: 14 }}>{editingGroup ? 'Edit Group' : 'Create New Group'}</h4>
              <div className="form-row">
                <input className="filter-input" placeholder="Group name" value={groupForm.name} onChange={e => setGroupForm({ ...groupForm, name: e.target.value })} data-testid="group-name-input" />
                <input className="filter-input" placeholder="Marketer names (comma-separated)" value={groupForm.marketer_names}
                  onChange={e => setGroupForm({ ...groupForm, marketer_names: e.target.value })} style={{ flex: 2 }} data-testid="group-marketers-input" />
              </div>
              <div className="form-row" style={{ gap: 8 }}>
                {targetKeys.map(k => (
                  <div key={k} className="form-field" style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: '#6b7280' }}>{k.replace(/_/g, " ")}</label>
                    <input className="filter-input" type="number" min="0" value={groupForm.targets[k]}
                      onChange={e => setGroupForm({ ...groupForm, targets: { ...groupForm.targets, [k]: parseInt(e.target.value) || 0 } })} />
                  </div>
                ))}
              </div>
              <div className="form-actions">
                <button className="btn btn-sm btn-primary" onClick={() => editingGroup ? handleUpdateGroup(editingGroup) : handleCreateGroup()}>
                  <Check size={14} /> {editingGroup ? 'Update' : 'Create'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => { setShowGroupForm(false); setEditingGroup(null); }}><X size={14} /> Cancel</button>
              </div>
            </div>
          )}

          <div className="groups-list">
            {groups.map(g => (
              <div key={g.id} className="group-card" data-testid={`group-${g.id}`}>
                <div className="group-header" onClick={() => setExpandedGroup(expandedGroup === g.id ? null : g.id)}>
                  <div className="group-info">
                    <span className="group-name">{g.name}</span>
                    <span className="group-count">{(g.marketer_names || []).length} marketers</span>
                  </div>
                  <div className="group-targets">
                    {Object.entries(g.targets || {}).map(([k, v]) => (
                      <span key={k} className="target-chip">{k.replace(/_/g, " ").replace("promotional", "promo").replace("non promo", "non-promo")}: {v}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); startEdit(g); }}><Edit2 size={12} /></button>
                    <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.id); }}><Trash2 size={12} /></button>
                    {expandedGroup === g.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>
                {expandedGroup === g.id && (
                  <div className="group-body">
                    <div className="group-members">
                      <strong style={{ fontSize: 12, color: '#9ca3af' }}>Members:</strong>
                      {(g.marketer_names || []).map(n => (
                        <span key={n} className="chip active">{n}</span>
                      ))}
                      {(g.marketer_names || []).length === 0 && <span style={{ fontSize: 12, color: '#4b5563' }}>No members assigned</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {groups.length === 0 && <div className="empty-state"><Target size={40} /><div className="empty-text">No groups created yet</div></div>}
          </div>
        </div>
      )}
    </div>
  );
}
